import React from "react";
import { stylex } from "./stylex";
import { cssVariables } from "./css_variables";
import { Icon } from "./icon";
import { Row } from "./row";
import { Spacing } from "./spacing";
import { Text } from "./text";
import { Pressable } from "./pressable";

interface SwitchProps {
  testID?: string;
  value?: boolean;
  disabled?: boolean;
  /**
   * Can't be changed here, but still shows whether it is on: the colours stay
   * and only fade. `disabled` greys the switch out, which reads the same on or
   * off, so it suits a control that doesn't apply rather than a locked value.
   */
  readOnly?: boolean;
  onChange?: (value: boolean) => void;
  ariaLabel?: string;
}

export function Switch(props: SwitchProps) {
  const {
    testID,
    disabled,
    readOnly,
    value,
    onChange = () => {},
    ariaLabel,
  } = props;
  const locked = readOnly && !disabled;
  const [isFocused, setIsFocused] = React.useState(false);

  return (
    <div
      style={styles(
        "root",
        isFocused && "focused",
        value ? "rootChecked" : "rootUnchecked",
        disabled && "disabled",
        locked && "readOnly"
      )}
    >
      <input
        data-testid={testID}
        aria-label={ariaLabel}
        style={styles("input", locked && "inputReadOnly")}
        type="checkbox"
        disabled={disabled || readOnly}
        checked={value}
        onChange={() => onChange(!value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
      />
      <div
        style={styles(
          "slider",
          value ? "sliderChecked" : "sliderUnchecked",
          disabled && "sliderDisabled"
        )}
      >
        {value && (
          <Icon
            name="check"
            color={
              disabled
                ? cssVariables.backgroundGrey
                : cssVariables.backgroundPrimary
            }
          />
        )}
      </div>
    </div>
  );
}

interface LabeledSwitchProps extends SwitchProps {
  label: string;
}

export function LabeledSwitch(props: LabeledSwitchProps) {
  const { testID, value, disabled, onChange = () => {}, label } = props;
  return (
    <Pressable onPress={() => onChange(!value)}>
      <Row alignItems="center">
        <Switch
          testID={testID}
          disabled={disabled}
          value={value}
          onChange={onChange}
        />
        <Spacing width={8} />
        <Text fontSize="sm" color="muted">
          {label}
        </Text>
      </Row>
    </Pressable>
  );
}

const styles = stylex.create({
  root: {
    position: "relative",
    borderRadius: 999,
    width: 56,
    height: 32,
    transition: "0.2s",
    boxSizing: "border-box",
    border: `2px solid transparent`,
  },
  rootChecked: {
    backgroundColor: cssVariables.backgroundPrimary,
  },
  rootUnchecked: {
    backgroundColor: cssVariables.backgroundGrey,
  },
  focused: {
    border: `2px solid ${cssVariables.borderColorFocus}`,
  },
  input: {
    opacity: 0,
    width: "100%",
    height: "100%",
    margin: 0,
    padding: 0,
    position: "relative",
    zIndex: 1,
    cursor: "pointer",
  },
  slider: {
    top: 0,
    position: "absolute",
    borderRadius: 999,
    width: 28,
    height: 28,
    backgroundColor: "white",
    transition: "0.2s",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  sliderUnchecked: {
    left: 0,
  },
  sliderChecked: {
    left: 24,
  },
  checkmark: {},
  sliderDisabled: {},
  readOnly: {
    opacity: 0.5,
  },
  inputReadOnly: {
    cursor: "auto",
  },
  disabled: {
    cursor: "auto",
    boxShadow: "inset 0 0 490px rgba(0, 0, 0, 0.03)",
    backgroundColor: cssVariables.backgroundGrey,
  },
});
